import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useMedplumCaseloadClients } from '../hooks/useMedplumCaseloadClients';
import type { Client, ClientGroup } from '../pages/caseload/types';

interface ClientsContextValue {
  clients: Client[];
  /** All patients in directory scope for the Clients tab (see shouldMergeDirectoryOnlyCaseloadClients). */
  allClients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  getClientById: (id: string) => Client | undefined;
  /** Client groups for caseload row merging (FHIR-derived). */
  clientGroups: ClientGroup[];
  /** True while initial Medplum caseload fetch runs. */
  caseloadLoading: boolean;
  caseloadError: Error | null;
  /** Re-fetch caseload from Medplum (e.g. after transfer fulfillment). */
  reloadCaseload: () => void;
}

const ClientsContext = createContext<ClientsContextValue | null>(null);

/**
 * Caseload + session-only chart helpers backed by Medplum (`useMedplumCaseloadClients`).
 */
export function ClientsProvider({ children }: { children: ReactNode }): JSX.Element {
  const { clients: loaded, allClients: loadedAllClients, groups: loadedGroups, loading, error, reload } =
    useMedplumCaseloadClients();
  const [clients, setClients] = useState<Client[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [clientGroups, setClientGroups] = useState<ClientGroup[]>([]);

  useEffect(() => {
    setClients(loaded);
    setAllClients(loadedAllClients);
    setClientGroups(loadedGroups);
  }, [loaded, loadedAllClients, loadedGroups]);

  const getClientById = useCallback(
    (id: string) => allClients.find((c) => c.id === id) ?? clients.find((c) => c.id === id),
    [allClients, clients],
  );

  const value = useMemo(
    () => ({
      clients,
      allClients,
      setClients,
      getClientById,
      clientGroups,
      caseloadLoading: loading,
      caseloadError: error,
      reloadCaseload: reload,
    }),
    [clients, allClients, getClientById, clientGroups, loading, error, reload],
  );

  return <ClientsContext.Provider value={value}>{children}</ClientsContext.Provider>;
}

export function useClients(): ClientsContextValue {
  const ctx = useContext(ClientsContext);
  if (!ctx) {
    throw new Error('useClients must be used within ClientsProvider');
  }
  return ctx;
}
